import {
    useEffect,
    useId,
    useLayoutEffect,
    useRef,
    useState,
    type KeyboardEvent,
    type RefObject,
    type ReactNode,
} from "react";
import { ArrowDown, Loader2, SendHorizontal, Square, ThumbsDown, ThumbsUp } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { cn } from "../ui/utils";
import { useLanguage } from "../../i18n/language-provider";
import { useExpertMode } from "../../hooks/useExpertMode";
import { useMediaQuery } from "../../hooks/useMediaQuery";
import {
    getLocation,
    getPendingNavigation,
    hasUnsavedWork,
    navigate,
    usePendingNavigation,
    useLocation,
    useRoute,
} from "../../navigation/history";
import type { AppRoute } from "../../navigation/routes";
import type { AssistantHandover } from "../../services/assistantApi";
import {
    cancelAssistantHandover,
    ensureAssistantLoaded,
    handOverAssistantChat,
    newAssistantConversation,
    openAssistantHandover,
    rateAssistantAnswer,
    sendAssistantQuestion,
    setAssistantDraft,
    setAssistantHandoverNote,
    stopAssistantAnswer,
    useAssistantChat,
    type AssistantChatSnapshot,
    type ChatAnswer,
    type ChatProblem,
} from "../../services/assistantChat";
import { LEGAL_CANONICAL_PATH } from "../legal/legalContent";
import {
    ACTIVE_CONNECTION_CHANGED_TOAST_ID,
    activeConnectionChangedCopy,
    toastActiveConnectionChanged,
} from "../pms/ActiveConnectionChangedNotice";
import { AssistantAnswer } from "./AssistantAnswer";
import { answerPlainText } from "./assistant-answer";
import {
    assistantChatCopy,
    assistantOutcomeNote,
    assistantRefusalProblem,
    assistantStreamErrorProblem,
    assistantSuggestions,
    assistantToolStatus,
    type AssistantChatCopy,
    type AssistantProblem,
} from "./assistant-chat-copy";
import type { ResolvedAssistantLink } from "./assistant-links";
import { buildAssistantPageContext } from "./assistant-page-context";
import { strategyHelpCopy, type StrategyHelpCopy } from "./strategy-help-copy";
import { SUPPORT_EMAIL, SUPPORT_RESPONSE_HOURS } from "./support-contact";

/** How close to the end the transcript counts as following it: about two lines. */
const PINNED_SLACK_PX = 48;
/** The questions left today are said once this few remain. */
const FEW_QUESTIONS_LEFT = 5;
const LINK_CLASS = "text-primary underline underline-offset-2 transition-colors duration-150 hover:text-foreground";

type FocusTarget = "composer" | "startNew" | "note";

interface AssistantChatPanelProps {
    /** The property the conversation is about; nothing is read without one. */
    connectionId: number | null;
    /** Where support's answer to a handover goes; null when the status could not say. */
    contactEmail: string | null;
    /** What the window focuses when it opens: the composer, or what stands in its place. */
    focusRef: RefObject<HTMLElement | null>;
    /** A file was pasted or dropped on the window, which the assistant does not read. */
    filesRefused: boolean;
    /** Leaves for a screen an answer links to; the shell decides whether it is open. */
    onNavigate?: (route: AppRoute) => void;
    /** The shell's verdict on a screen, so a link it would refuse reads as text. */
    isPageAllowed?: (route: AppRoute) => boolean | "pending";
    /** Puts the window aside: on a phone, once a link's page has opened under it. */
    onMinimize: () => void;
    /** The conversation became an ordinary question to support. */
    onHandedOver: (request: AssistantHandover["request"]) => void;
    /** The question form, with what was typed, where the assistant cannot answer. */
    onAskSupport: (text: string) => void;
    /** The composer took or lost the focus (a phone's keyboard then takes the screen). */
    onComposerFocusChange?: (focused: boolean) => void;
}

/**
 * The server's `hasAnsweredQuestion`: a question whose answer has ended,
 * however it ended — a refusal or a failure is exactly what support is for.
 */
function hasAnswer(chat: AssistantChatSnapshot): boolean {
    let asked = false;
    for (const message of chat.messages) {
        if (message.role === "user") asked = true;
        else if (asked && message.status !== "streaming") return true;
    }
    return false;
}

function problemText(problem: ChatProblem, chat: AssistantChatSnapshot, copy: AssistantChatCopy, t: Parameters<typeof activeConnectionChangedCopy>[0]): AssistantProblem {
    switch (problem.kind) {
        case "refusal":
            return assistantRefusalProblem({
                code: problem.code,
                limit: problem.limit,
                maxMessageChars: chat.limits?.maxMessageChars,
            }, copy);
        case "stream":
            return assistantStreamErrorProblem(problem.code, copy);
        case "connectionChanged":
            return { text: activeConnectionChangedCopy(t).pageDescription, action: null };
        case "network":
            return { text: copy.errorGeneric.replace("{email}", SUPPORT_EMAIL), action: null };
    }
}

/** Why a handover was refused, in the words the question form uses for the same refusals. */
function handoverRefusalText(
    code: string | null,
    copy: AssistantChatCopy,
    help: StrategyHelpCopy,
    t: Parameters<typeof activeConnectionChangedCopy>[0],
): string {
    switch (code) {
        case "ACTIVE_CONNECTION_CHANGED":
            return activeConnectionChangedCopy(t).pageDescription;
        case "HELP_REQUEST_LIMIT":
            return help.errorLimit.replace("{email}", SUPPORT_EMAIL);
        case "NO_CONTACT_EMAIL":
            return help.errorNoEmail.replace("{email}", SUPPORT_EMAIL);
        case "ASSISTANT_CONVERSATION_EMPTY":
            return copy.errorHandOverEmpty;
        case "ASSISTANT_ALREADY_HANDED_OVER":
            return copy.errorHandOverAlready;
        case "ASSISTANT_BUSY":
            return copy.errorHandOverBusy;
        case "ASSISTANT_MESSAGE_PERSONAL_DATA":
            return copy.errorPersonalData;
        case "ASSISTANT_CONVERSATION_NOT_FOUND":
        case "ASSISTANT_CONVERSATION_CLOSED":
            return copy.errorConversationGone;
        default:
            return help.errorGeneric.replace("{email}", SUPPORT_EMAIL);
    }
}

/**
 * The live assistant in the "Ask Optivo" window: the conversation, and the
 * composer under it.
 *
 * The conversation is the module store's (`services/assistantChat.ts`), not
 * this component's: the window can be minimised, closed or hidden while an
 * answer streams in, and what is being typed — the question and a handover's
 * note — survives all of it. This draws it and asks for what the operator
 * does.
 *
 * Every turn the assistant writes is labelled as the AI's (`data-ai-generated`),
 * and so is the assistant's introduction and the line over the composer — the
 * window says it is an AI wherever an answer is read (EU AI Act Art. 50). The
 * composer says not to write guests' names or contacts, and who processes the
 * conversation.
 *
 * Only the transcript scrolls, and it follows the newest line only while the
 * operator is at its end; read further up, a "Jump to latest" button waits
 * instead. An empty conversation is shown from its head and offers no such
 * button, there being no latest line to go to. The switch between the ways to
 * ask is the window's, drawn above this panel and outside its scroll, so it
 * never leaves the screen with the conversation. The visible transcript is not
 * a live region: an answer is announced
 * once, by a hidden region of its own, in the words the server stored once the
 * read-back after it brings them — never as it streamed, the model's own words
 * not yet scrubbed — and where that read fails, only that an answer arrived. A
 * conversation read back on its own is never announced. The status line under
 * an answer being written says what the assistant is reading, once per tool.
 *
 * Enter sends where there is a fine pointer and nothing is being composed; on
 * a touch screen Enter is a new line and the button sends. Send and Stop are
 * one button, so the focus stays on it either way.
 *
 * Handing the conversation over is confirmed in place, never in a second
 * dialog, and turns the composer into "Start a new conversation".
 */
export function AssistantChatPanel({
    connectionId,
    contactEmail,
    focusRef,
    filesRefused,
    onNavigate,
    isPageAllowed,
    onMinimize,
    onHandedOver,
    onAskSupport,
    onComposerFocusChange,
}: AssistantChatPanelProps) {
    const { t, language, locale } = useLanguage();
    const copy = assistantChatCopy(t);
    const help = strategyHelpCopy(t);
    const ids = useId();
    const chat = useAssistantChat();
    const route = useRoute();
    const location = useLocation();
    const pendingNavigation = usePendingNavigation();
    const [expertMode] = useExpertMode();
    const coarsePointer = useMediaQuery("(pointer: coarse)");
    const phone = useMediaQuery("(max-width: 639px)");
    const reducedMotion = useMediaQuery("(prefers-reduced-motion: reduce)");
    const composer = useRef<HTMLTextAreaElement | null>(null);
    const startNew = useRef<HTMLButtonElement | null>(null);
    const noteBox = useRef<HTMLTextAreaElement | null>(null);
    const scroller = useRef<HTMLDivElement | null>(null);
    const [pinned, setPinned] = useState(true);
    const [focusNext, setFocusNext] = useState<FocusTarget | null>(null);
    // The announcement standing when the window opened is old news.
    const seenAnnouncement = useRef(chat.announcement?.id ?? 0);
    const linkFollowed = useRef<{ idx: number; sawPending: boolean } | null>(null);

    const answerLanguage = language === "cs" || language === "en"
        ? language
        : locale?.toLowerCase().startsWith("cs") ? "cs" : "en";
    const hours = String(SUPPORT_RESPONSE_HOURS);
    const ready = chat.loaded || chat.loadFailed;
    const offline = chat.loaded && !chat.available;
    const handedOver = chat.status === "handed_over";
    const canHandOver = chat.status === "open" && chat.conversationId !== null && !chat.streaming && hasAnswer(chat);
    const canStartOver = !handedOver && (chat.messages.length > 0 || chat.conversationId !== null);

    useEffect(() => {
        if (connectionId !== null) void ensureAssistantLoaded(connectionId);
    }, [connectionId]);

    // The window focuses whatever stands where the question is written: the
    // composer, the handover's note or "Start a new conversation". One that
    // leaves stops being the target, so the window never focuses a box that
    // is no longer on the page.
    const bindFocusTarget = <T extends HTMLElement>(slot: { current: T | null }) => (element: T | null) => {
        if (!element && focusRef.current === slot.current) focusRef.current = null;
        slot.current = element;
        if (element) focusRef.current = element;
    };
    const bindComposer = bindFocusTarget(composer);
    const bindStartNew = bindFocusTarget(startNew);
    const bindNote = bindFocusTarget(noteBox);

    // A move the operator made is followed by the focus once what it moves to is drawn.
    useEffect(() => {
        if (!focusNext) return;
        const target = focusNext === "composer" ? composer.current : focusNext === "startNew" ? startNew.current : noteBox.current;
        if (!target) return;
        target.focus();
        setFocusNext(null);
        // Tried again whenever what is drawn in the composer's place changes.
    }, [focusNext, chat.status, chat.handover.open]);

    // On a phone the window steps aside once a link's page has opened, and
    // stays where the move was refused — outright or at the discard question.
    useEffect(() => {
        const followed = linkFollowed.current;
        if (!followed) return;
        if (location.idx !== followed.idx) {
            linkFollowed.current = null;
            onMinimize();
            return;
        }
        if (pendingNavigation) followed.sawPending = true;
        else if (followed.sawPending) linkFollowed.current = null;
    }, [location, pendingNavigation, onMinimize]);

    const followLink = (link: ResolvedAssistantLink) => {
        linkFollowed.current = phone ? { idx: getLocation().idx, sawPending: false } : null;
        if (onNavigate) onNavigate(link.route);
        else navigate(link.href);
        const followed = linkFollowed.current;
        if (followed && getLocation().idx === followed.idx && !getPendingNavigation()) linkFollowed.current = null;
    };

    // The transcript follows its end while the operator is there. An empty
    // conversation has no end to follow: it is shown from its head, where the
    // assistant says what it is, however it came onto the screen — emptied by
    // "New conversation", or drawn again by a window that was closed,
    // minimised or left for the question form. Kept at its end, it hid its
    // head on every one of those ways (Hotel Zámek Svijany, OPT-56, Oct 2026).
    const last = chat.messages[chat.messages.length - 1];
    const conversationEmpty = chat.messages.length === 0;
    const contentKey = `${chat.messages.length}:${last?.id ?? ""}:${last?.text.length ?? 0}:${chat.toolStatus?.name ?? ""}:${chat.streaming}`;
    const scrollToEnd = (smooth: boolean) => {
        const element = scroller.current;
        if (!element) return;
        if (typeof element.scrollTo === "function") {
            element.scrollTo({ top: element.scrollHeight, behavior: smooth && !reducedMotion ? "smooth" : "auto" });
        } else {
            element.scrollTop = element.scrollHeight;
        }
    };
    useLayoutEffect(() => {
        if (conversationEmpty) {
            if (scroller.current) scroller.current.scrollTop = 0;
        } else if (pinned) {
            scrollToEnd(false);
        }
        // Only new content moves the transcript; the reading position is the operator's.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [contentKey]);
    const onScroll = () => {
        const element = scroller.current;
        if (!element) return;
        setPinned(element.scrollHeight - element.scrollTop - element.clientHeight <= PINNED_SLACK_PX);
    };

    const ask = async (text: string) => {
        const question = text.trim();
        if (!question || !ready || offline || chat.streaming || connectionId === null) return;
        setPinned(true);
        const page = buildAssistantPageContext(getLocation(), { unsavedEdits: hasUnsavedWork(), expertMode });
        const result = await sendAssistantQuestion(question, page, answerLanguage);
        if (!result.ok) {
            toastActiveConnectionChanged(result.error, t, { send: true, id: ACTIVE_CONNECTION_CHANGED_TOAST_ID });
        }
    };

    const onComposerKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
        if (event.key !== "Enter" || event.shiftKey || event.altKey) return;
        if (event.nativeEvent.isComposing || event.keyCode === 229) return;
        // A touch keyboard's Enter is a new line; the button sends.
        if (coarsePointer && !event.ctrlKey && !event.metaKey) return;
        event.preventDefault();
        void ask(chat.draft);
    };

    // Asked before the old conversation is closed: the button pressed goes
    // with the conversation at once, and the close may take its time.
    const startOver = async () => {
        setFocusNext("composer");
        await newAssistantConversation();
    };

    const rate = async (answer: ChatAnswer, value: "up" | "down") => {
        const saved = await rateAssistantAnswer(answer.id, answer.feedback === value ? null : value);
        if (!saved) toast.error(copy.feedbackFailed);
    };

    const confirmHandover = async () => {
        const result = await handOverAssistantChat();
        if (result.ok) {
            onHandedOver(result.handover.request);
            setFocusNext("startNew");
        } else {
            toastActiveConnectionChanged(result.error, t, { send: true, id: ACTIVE_CONNECTION_CHANGED_TOAST_ID });
        }
    };

    const problem = chat.problem ? problemText(chat.problem, chat, copy, t) : offline
        ? { text: copy.errorUnavailable, action: "askSupport" as const }
        : null;

    const announcement = chat.announcement && chat.announcement.id > seenAnnouncement.current
        ? (() => {
            const answer = chat.announcement.answer;
            // The answer as stored could not be read: only that one arrived.
            if (!answer) return `${copy.answered}.`;
            const words = [answerPlainText(answer.text), assistantOutcomeNote(answer, copy)].filter(Boolean).join(" ");
            return `${copy.assistantLabel}: ${words}`;
        })()
        : "";

    const remaining = chat.limits?.messagesPerDay != null
        ? Math.max(0, chat.limits.messagesPerDay - chat.limits.usedToday)
        : null;
    const questionsLeft = remaining !== null && remaining <= FEW_QUESTIONS_LEFT
        ? ` ${copy.questionsLeft.replace("{count}", String(remaining))}`
        : "";

    const turns = chat.messages.map((message) => {
        if (message.role === "user") {
            return (
                <li key={message.id} className="flex justify-end text-sm">
                    <div className="min-w-0 max-w-[85%] rounded-md bg-secondary px-3 py-2 text-secondary-foreground">
                        <span className="sr-only">{copy.youLabel}: </span>
                        <p className="whitespace-pre-wrap break-words text-sm">{message.text}</p>
                    </div>
                </li>
            );
        }
        const live = chat.streaming && message === last;
        const note = live ? null : assistantOutcomeNote(message, copy);
        const showText = message.stopReason !== "refusal" && message.text !== "";
        const rateable = !live && showText && !message.id.startsWith("local-")
            && (message.status === "complete" || message.status === "interrupted");
        return (
            <li key={message.id} data-ai-generated="true" aria-busy={live ? true : undefined} className="space-y-1.5 text-sm">
                <p className="text-xs text-muted-foreground">{copy.assistantLabel}</p>
                {showText && (
                    <AssistantAnswer text={message.text} linkContext={{ isPageAllowed }} onFollowLink={followLink} />
                )}
                {live && (chat.toolStatus || !showText) && (
                    <p role="status" className="flex items-center gap-2 text-xs text-muted-foreground">
                        <Loader2 className="size-4 shrink-0 animate-spin" aria-hidden />
                        {chat.toolStatus ? assistantToolStatus(chat.toolStatus, copy, locale) : copy.statusThinking}
                    </p>
                )}
                {note && <p className="text-xs text-muted-foreground">{note}</p>}
                {rateable && (
                    <div className="-ml-2 flex gap-0.5">
                        <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="size-10 text-muted-foreground sm:size-8"
                            aria-label={copy.feedbackUp}
                            aria-pressed={message.feedback === "up"}
                            onClick={() => void rate(message, "up")}
                        >
                            <ThumbsUp className={cn("size-4", message.feedback === "up" && "text-primary")} aria-hidden />
                        </Button>
                        <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="size-10 text-muted-foreground sm:size-8"
                            aria-label={copy.feedbackDown}
                            aria-pressed={message.feedback === "down"}
                            onClick={() => void rate(message, "down")}
                        >
                            <ThumbsDown className={cn("size-4", message.feedback === "down" && "text-primary")} aria-hidden />
                        </Button>
                    </div>
                )}
            </li>
        );
    });

    const actionButton = (action: AssistantProblem["action"]) => {
        switch (action) {
            case "newConversation":
                return (
                    <Button type="button" variant="outline" size="sm" className="h-10 sm:h-8" onClick={() => void startOver()}>
                        {copy.startNewConversation}
                    </Button>
                );
            case "askSupport":
                return (
                    <Button type="button" variant="outline" size="sm" className="h-10 sm:h-8" onClick={() => onAskSupport(chat.draft)}>
                        {copy.askSupportInstead}
                    </Button>
                );
            case "handOver":
                return canHandOver ? (
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="h-10 sm:h-8"
                        onClick={() => {
                            openAssistantHandover();
                            setFocusNext("note");
                        }}
                    >
                        {copy.handOver}
                    </Button>
                ) : null;
            default:
                return null;
        }
    };

    const handoverBody = (contactEmail ? copy.handOverBody.replace("{email}", contactEmail) : copy.handOverBodyAccount)
        .replace("{hours}", hours);

    let footer: ReactNode;
    if (handedOver) {
        const caseNumber = chat.handedOver?.caseNumber ?? "";
        const sentence = chat.handover.result
            ? (contactEmail ? copy.handedOver.replace("{email}", contactEmail) : copy.handedOverAccount)
                .replace("{case}", caseNumber).replace("{hours}", hours)
            : copy.handedOverEarlier.replace("{case}", caseNumber);
        footer = (
            <div className="space-y-2">
                <p className="text-sm">{sentence}</p>
                <Button ref={bindStartNew} type="button" className="w-full sm:w-auto" onClick={() => void startOver()}>
                    {copy.startNewConversation}
                </Button>
            </div>
        );
    } else if (chat.handover.open) {
        footer = (
            <div role="group" aria-labelledby={`${ids}-handover-title`} className="space-y-2">
                <p id={`${ids}-handover-title`} className="text-sm font-medium">{copy.handOverTitle}</p>
                <p className="text-xs text-muted-foreground">{handoverBody}</p>
                <div className="space-y-1">
                    <Label htmlFor={`${ids}-note`} className="text-xs">{copy.handOverNoteLabel}</Label>
                    <Textarea
                        ref={bindNote}
                        id={`${ids}-note`}
                        value={chat.handover.note}
                        maxLength={1000}
                        rows={2}
                        className="max-h-32 min-h-10"
                        onChange={(event) => setAssistantHandoverNote(event.target.value)}
                    />
                </div>
                {chat.handover.problem && (
                    <p role="alert" className="text-sm text-destructive">
                        <span className="font-medium">{copy.handOverFailedTitle}.</span>{" "}
                        {handoverRefusalText(chat.handover.problem.code, copy, help, t)}
                    </p>
                )}
                <div className="flex flex-wrap justify-end gap-2">
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="h-10 sm:h-8"
                        onClick={() => {
                            cancelAssistantHandover();
                            setFocusNext("composer");
                        }}
                    >
                        {copy.handOverCancel}
                    </Button>
                    <Button
                        type="button"
                        size="sm"
                        className="h-10 sm:h-8"
                        disabled={chat.handover.sending}
                        onClick={() => void confirmHandover()}
                    >
                        {chat.handover.sending ? copy.handingOver : copy.handOverConfirm}
                    </Button>
                </div>
            </div>
        );
    } else {
        const canSend = ready && !offline && chat.draft.trim() !== "";
        footer = (
            <div className="space-y-2">
                <p data-slot="assistant-ai-notice" className="hidden text-xs text-muted-foreground @min-[30rem]/ask-optivo:block">
                    {copy.aiNotice}
                </p>
                <div className="flex items-end gap-2">
                    <Label htmlFor={`${ids}-composer`} className="sr-only">{copy.composerLabel}</Label>
                    <Textarea
                        ref={bindComposer}
                        id={`${ids}-composer`}
                        value={chat.draft}
                        maxLength={chat.limits?.maxMessageChars ?? undefined}
                        rows={2}
                        className="max-h-40 min-h-10 flex-1"
                        placeholder={copy.composerPlaceholder}
                        aria-describedby={`${ids}-hint`}
                        onChange={(event) => setAssistantDraft(event.target.value)}
                        onKeyDown={onComposerKeyDown}
                        onFocus={() => onComposerFocusChange?.(true)}
                        onBlur={() => onComposerFocusChange?.(false)}
                    />
                    <Button
                        type="button"
                        size="icon"
                        className="size-10 shrink-0 sm:size-8"
                        aria-label={chat.streaming ? copy.stop : copy.send}
                        aria-disabled={!chat.streaming && !canSend ? true : undefined}
                        onClick={() => (chat.streaming ? stopAssistantAnswer() : void ask(chat.draft))}
                    >
                        {chat.streaming ? <Square className="size-4" aria-hidden /> : <SendHorizontal className="size-4" aria-hidden />}
                    </Button>
                </div>
                <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
                    <p id={`${ids}-hint`} className="min-w-0 flex-1 basis-48 text-xs text-muted-foreground">
                        <span className="@min-[30rem]/ask-optivo:hidden">{copy.aiNoticeShort} </span>
                        {copy.composerHint}{" "}
                        <a href={LEGAL_CANONICAL_PATH.privacy} target="_blank" rel="noopener noreferrer" className={LINK_CLASS}>
                            {copy.privacyLink}
                        </a>
                        {questionsLeft}
                    </p>
                    {(canStartOver || canHandOver) && (
                        <div className="flex w-full gap-1 @min-[30rem]/ask-optivo:w-auto">
                            {canStartOver && (
                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    className="h-10 flex-1 px-2 text-xs sm:h-8 @min-[30rem]/ask-optivo:flex-none"
                                    onClick={() => void startOver()}
                                >
                                    {copy.newConversation}
                                </Button>
                            )}
                            {canHandOver && (
                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    className="h-10 flex-1 px-2 text-xs sm:h-8 @min-[30rem]/ask-optivo:flex-none"
                                    onClick={() => {
                                        openAssistantHandover();
                                        setFocusNext("note");
                                    }}
                                >
                                    {copy.handOver}
                                </Button>
                            )}
                        </div>
                    )}
                </div>
            </div>
        );
    }

    const empty = ready && chat.messages.length === 0 && !handedOver;

    return (
        <div data-slot="assistant-chat" className="flex min-h-0 flex-1 flex-col">
            <div className="relative flex min-h-0 flex-1 flex-col">
                <div
                    ref={scroller}
                    data-slot="assistant-transcript"
                    className="min-h-0 flex-1 space-y-4 overflow-y-auto overflow-x-hidden p-4"
                    onScroll={onScroll}
                >
                    {!ready && chat.loading && (
                        <p className="flex items-center gap-2 text-sm text-muted-foreground">
                            <Loader2 className="size-4 animate-spin" aria-hidden />
                            {copy.loading}
                        </p>
                    )}
                    {chat.loadFailed && !chat.loaded && (
                        <div className="space-y-2">
                            <p className="text-sm text-muted-foreground">{copy.loadFailed}</p>
                            <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                className="h-10 sm:h-8"
                                onClick={() => {
                                    if (connectionId !== null) void ensureAssistantLoaded(connectionId);
                                }}
                            >
                                {copy.retry}
                            </Button>
                        </div>
                    )}
                    {empty && (
                        <div className="space-y-3">
                            <div data-ai-generated="true" className="space-y-1.5">
                                <p className="text-xs text-muted-foreground">{copy.assistantLabel}</p>
                                <p className="text-sm leading-relaxed">{copy.intro}</p>
                                {chat.retentionDays !== null && (
                                    <p className="text-xs text-muted-foreground">
                                        {copy.retention.replace("{days}", String(chat.retentionDays))}
                                    </p>
                                )}
                            </div>
                            {!offline && (
                                <div className="space-y-1.5">
                                    <p className="text-xs text-muted-foreground">{copy.suggestionsTitle}</p>
                                    <ul className="flex flex-col items-start gap-1.5 text-sm">
                                        {assistantSuggestions(route?.page ?? null, copy).map((suggestion) => (
                                            <li key={suggestion} className="max-w-full text-sm">
                                                <Button
                                                    type="button"
                                                    variant="outline"
                                                    size="sm"
                                                    className="h-auto min-h-10 max-w-full whitespace-normal py-1.5 text-left text-sm sm:min-h-8"
                                                    onClick={() => void ask(suggestion)}
                                                >
                                                    {suggestion}
                                                </Button>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </div>
                    )}
                    {chat.messages.length > 0 && (
                        <ul aria-label={copy.transcriptLabel} className="space-y-4 text-sm">
                            {turns}
                        </ul>
                    )}
                </div>
                {/* Offered over a conversation only: an empty one has no latest line. */}
                {!pinned && !conversationEmpty && (
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="absolute bottom-2 left-1/2 h-10 -translate-x-1/2 bg-popover sm:h-8"
                        onClick={(event) => {
                            // It goes away as it acts: pressed from the keyboard, the
                            // focus it held goes where the question is written. A tap
                            // or a click (`detail` counts them; a key press is 0)
                            // leaves it, or a phone opens its keyboard over the answer
                            // the operator went to read.
                            const fromKeyboard = event.detail === 0 && document.activeElement === event.currentTarget;
                            if (fromKeyboard) focusRef.current?.focus({ preventScroll: true });
                            setPinned(true);
                            scrollToEnd(true);
                        }}
                    >
                        <ArrowDown className="size-4" aria-hidden />
                        {copy.jumpToLatest}
                    </Button>
                )}
            </div>
            {/* One announcement when an answer ends; never the pieces of one. */}
            <div data-slot="assistant-announcer" aria-live="polite" aria-atomic="true" className="sr-only">
                {announcement}
            </div>
            <div className="space-y-2 border-t border-border p-3 sm:pr-7">
                {filesRefused && (
                    <p className="text-xs text-muted-foreground">{copy.filesNotRead}</p>
                )}
                {problem && !handedOver && !chat.handover.open && (
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
                        <p role="alert" className="min-w-0 flex-1 basis-48 text-sm text-destructive">{problem.text}</p>
                        {actionButton(problem.action)}
                    </div>
                )}
                {footer}
            </div>
        </div>
    );
}
